import { createContext, useContext, useState } from 'react'
import { getApi } from './api-common'
import { TAddon, TDuration, TPlanSummary } from './cms-types'
import { orderToEvent, planToEvent } from './utils/planToEvent'
import { addonToEvent } from './utils/addonToEvent'
import { AddonSlug, addonToProductMapping, PlanSlug, planToParent } from './planSlugs'
import { AddressType } from './types/address-type'
import { Project } from './project'
import { TAdditionalService, TOrder, TRequire3ds } from './types/order-type'
import { getPriceIds } from 'components/util/getPriceIds'
import { coupon } from './useCalculatePrice'
import { BusinessType } from './types/business-type'
import { Utils } from './utils'
import dataRelay from 'data-relay'
import Cookies from 'js-cookie'

export type BagItem = {
  slug: PlanSlug | null
  duration: TDuration
  addons: AddonSlug[]
  addonSources: Record<Partial<AddonSlug>, string>
}

export type CheckoutAPIType = {
  reference_id?: string
  billing_request_id?: string
  payment_intent?: string
  customer_id?: string
  payment_type: string
  customer_email: string
  business: {
    business_type: string
    legal_name: string
    country_of_corporation: string
    trading_name: string | null
    company_number: string | null
    description: string | null
  }
  billing_address: AddressType
  address: AddressType
  product_id: string
  addons?: string[]
}

export type Require3dsType = (
  payment_intent: string,
  client_secret: string,
  customer_id: string,
) => Promise<{
  payment_intent?: string
  client_secret?: string
  customer_id: string
}>
export const getIsMailPlan = (plan: TPlanSummary | undefined) =>
  plan?.slug === 'Virtual-Office-London-Mail-Forwarding' ||
  plan?.slug === 'Virtual-Office-London'
export const trackService = (
  plan: string,
  service: string,
  postcheckout: boolean,
  url?: string,
) => {
  if (Project.dataRelay) {
    try {
      dataRelay.sendEvent(
        {
          event: postcheckout
            ? 'Post-checkout Business Service'
            : 'Business Service',
          location: document.location.href,
          plan: plan,
          source: url,
          sku: service,
        },
        { apiKey: Project.dataRelay },
      )
    } catch (e) {}
  }
}

export const trackOrder = (order: TOrder) => {
  if (Project.dataRelay) {
    try {
      if (order.additional_services?.length) {
        dataRelay.sendEvent(
          {
            event: 'Checkout with Services',
            location: document.location.href,
            services: order.additional_services?.map(
              (v) => v.partner_offer_product,
            ),
            sku: order.plan,
          },
          { apiKey: Project.dataRelay },
        )
      }
      dataRelay.sendEvent(
        {
          addons: order.addons,
          event: 'Sale',
          propertyString1: order.payment_method?.payment_type,
          propertyString2: order.period,
          propertyString3: '1',
          services: order.additional_services?.map(
            (v) => v.partner_offer_product,
          ),
          sku: order.plan,
          source: 'Website',
          total:
            order.plan.toLowerCase().includes('annual') ||
            order.plan.toLowerCase().includes('yearly')
              ? order.total / 12
              : order.total,
        },
        { apiKey: Project.dataRelay },
      )
    } catch (e) {}
  }
}

export const ShoppingBagContext = createContext({
  setValue: (value: BagItem) => {
    // console.error('Tried to set bag', value)
  },
  value: { addons: [], addonSources:{}, duration: 'month', slug: null } as BagItem,
})

export default function () {
  const { setValue, value } = useContext(ShoppingBagContext)

  const [addonDetails, setAddonDetails] = useState<TAddon[]>([])

  const removePlan = (plan: TPlanSummary) => {
    if (value.slug) {
      getApi().trackEvent(
        'remove_from_cart_ga4',
        planToEvent(plan, value.duration),
      )

      setValue({ addons: value.addons || [], duration: 'month', slug: null })
    }
  }

  const clearBasket = () => {
    setValue({ addons: [], duration: 'month', slug: null })
  }
  const removePlanAddon = (addonSlug: string, data: TAddon) => {
    const newPlanAddons = addonDetails?.filter((v) => v.slug !== addonSlug)
    setAddonDetails(newPlanAddons)
    Utils.setProperties({
      checkout_offers: newPlanAddons?.map((v) => v.offer_category).join(','),
    })
    if (
      value.addons.find((v) => {
        return v === addonSlug
      })
    ) {
      getApi().trackEvent(
        'remove_from_cart_ga4',
        addonToEvent(data, value.duration),
      )
      setValue({
        ...value,
        addons: value.addons.filter((v) => {
          return v !== addonSlug
        }),
      })
    }
  }

  const addPlan = (
    slug: PlanSlug,
    duration: TDuration = 'month',
    summary: TPlanSummary,
  ) => {
    // do nothing if plan is already in bag
    if (value.slug === slug) {
      return
    }

    // @ts-ignore
    getApi().trackEvent('add_to_cart_ga4', planToEvent(summary, duration))

    if (
      // Attempting to add a plan that could also be an addon to a non-empty basket
      addonToProductMapping[slug] &&
      value.slug
    ) {
      //Only add the addon if it doesn't exist
      if (!value.addons.includes(addonToProductMapping[slug]!)) {
        setValue({
          ...value,
          addons: value.addons.concat([addonToProductMapping[slug]!]),
        })
      }
    } else {
      setValue({
        ...value,
        addons:
          // @ts-ignore
          value.slug &&
          addonToProductMapping[value.slug] &&
          !value.addons.includes(addonToProductMapping[value.slug]!)
            ? // Attempting to add a plan to a non-empty basket that has a product which could be an addon
              value.addons.concat([addonToProductMapping[value.slug]!])
            : value.addons,
        duration,
        slug,
      })
    }
  }

  const replacePlan = (
    slug: PlanSlug,
    duration: TDuration = 'month',
    summary: TPlanSummary,
  ) => {
    setValue({ addons: [], duration, slug: slug })
  }

  const checkout = (
    data: CheckoutAPIType,
    plans: TPlanSummary[],
    addons: TAddon[],
    onRequire3ds: Require3dsType,
  ) => {
    const currentPlan = value.slug
    const currentAddons = value.addons

    const ids = getPriceIds(
      plans,
      addons,
      currentPlan,
      currentAddons,
      value.duration,
    )
    const planSummary = plans.find((v) => v.slug === value.slug)
    if (!ids?.product_id) {
      return Promise.reject('You have no products')
    }
    const additional_services = currentAddons.filter((v) =>
      addons.find(
        (addon) => v === addon.slug && !!addon.isService && !addon.serviceLink,
      ),
    )
    const body = {
      ...data,
      additional_services,
      addons: ids.addons?.filter((v) => !!v),
      business: {
        ...data.business,
        legal_name:
          data.business.business_type === BusinessType.SOLE_TRADER
            ? data.business.trading_name
            : data.business.legal_name,
      },
      google_clid: Cookies.get('_gcl_aw'),
      payment_type: coupon?.discountedTotal === 0 ? 'NONE' : data.payment_type,
      product_id: ids.product_id,
      reference_id: data.reference_id,
      tolt_referral: window.tolt_referral || undefined,
      voucher: coupon?.code,
    } as CheckoutAPIType

    return fetch(`${Project.api}checkout/`, {
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return Promise.resolve(response)
        } else {
          return Promise.reject(response.text())
        }
      })
      .then((res) => {
        return res.json()
      })
      .then(async (apiRes: TOrder | TRequire3ds) => {
        let res = apiRes
        if ((res as TRequire3ds).payment_intent) {
          res = res as TRequire3ds
          const { client_secret, customer_id, payment_intent } =
            await onRequire3ds(
              res.payment_intent,
              res.client_secret,
              res.customer_id,
            )
          if (!payment_intent) {
            throw 'Payment intent was not successful'
          }
          res = await fetch(`${Project.api}checkout/confirm`, {
            body: JSON.stringify({
              ...body,
              client_secret,
              customer_id,
              payment_intent,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })
            .then((response) => {
              // todo: standardise api response handling
              if (response.status >= 200 && response.status < 300) {
                return Promise.resolve(response)
              } else {
                return Promise.reject(response.text())
              }
            })
            .then((res) => {
              return res.json() as Promise<TOrder>
            })
        }
        res = res as TOrder
        if (res.order_id) {
          try {
            getApi().trackEvent(
              'purchase_ga4',
              orderToEvent(
                planSummary!,
                value.addons.map((v) =>
                  addons.find((addon) => addon.slug === v),
                ) as TAddon[],
                value.duration,
                res,
              ),
            )
          } catch (e) {
            console.error('Could not track purchase event', e)
          }
          const order = {
            ...res,
            // re-add services with explicit link
            additional_services: (res.additional_services || []).concat(
              currentAddons
                .filter((v) =>
                  addons.find(
                    (addon) => v === addon.slug && !!addon.serviceLink,
                  ),
                )
                .filter((v) => !!v)
                .map((v) => addons.find((addon) => v === addon.slug))
                .map((v) => ({
                  link_url: v?.serviceLink || '',
                  partner_offer_product: v?.slug || ('' as any),
                })),
            ),
          }
          order.additional_services?.forEach((service) => {
            trackService(order.plan, service?.partner_offer_product, false, value.addonSources?.[service.partner_offer_product])
          })
          trackOrder(order)
          getApi().storage.setItemSync('order', JSON.stringify(order))
          return res
        } else {
          throw 'Order was not created'
        }
      })
      .catch((e) => {
        throw e
      })
  }

  const updateDuration = (
    duration: TDuration = 'month',
    summary: TPlanSummary,
  ) => {
    // @ts-ignore
    getApi().trackEvent('add_to_cart_ga4', planToEvent(summary, duration))
    //todo: if existing basket or new product is virtual phone, replace with 020 add-on
    setValue({
      ...value,
      duration,
    })
  }

  const addPlanAddon = (addon: AddonSlug, data: TAddon) => {
    if (data.offer) {
      const newPlanAddons = addonDetails
        ?.filter((v) => v.slug !== addon)
        .concat([data])
      setAddonDetails(newPlanAddons)
      Utils.setProperties({
        checkout_offers: newPlanAddons?.map((v) => v.offer_category).join(','),
      })
    }
    if (value.addons.find((addonName) => addon === addonName)) {
      console.warn('Attempting to add already added add-on')
      return
    }

    getApi().trackEvent('add_to_cart_ga4', addonToEvent(data, value.duration))

    setValue({
      ...value,
      addons: value.addons.concat([addon]),
      addonSources: ({
        ...(value.addonSources||{}),
        [addon]: document.location.pathname
      }),
    })
  }

  const hasPlanInBag = (slug: string) => {
    return value.slug === slug
  }

  const parentPlan = planToParent[value.slug]
  let itemsInBag = value.slug ? 1 + value.addons.length : 0
  if (parentPlan) {
    itemsInBag = itemsInBag + 1
  }
  const addService = (order: TOrder, service: TAddon) => {
    const body = {
      additional_service: service.slug,
      company_name: order.company_details.company_name,
      company_number: order.company_details.company_number,
      customer_email: order.contact_details.email,
      customer_id: order.customer_id,
      first_name: order.contact_details.first_name,
      last_name: order.contact_details.last_name,
      phone_number: order.contact_details.phone_number,
    }
    if (service.serviceLink) {
      return Promise.resolve({
        link_url: service.serviceLink,
        partner_offer_product: service.slug,
      })
    }
    return fetch(`${Project.api}offers/referrals`, {
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then((res) => res.json())
      .then((res) => {
        trackService(
          order?.plan?.toLowerCase(),
          service?.slug?.toLowerCase(),
          true,
        )
        return res as TAdditionalService | null
      })
  }

  return {
    addPlan,
    addPlanAddon,
    addService,
    checkout,
    clearBasket,
    hasPlanInBag,
    itemsInBag,
    removePlan,
    removePlanAddon,
    replacePlan,
    updateDuration,
    value,
  }
}
